import { useEffect, useState, useMemo } from "react";
import { Users, Plus, Edit3, Trash2, FileText, AlertTriangle, TrendingUp, Euro, Calendar, Building2, User, X, Download, IdCard, RotateCcw, FileSignature, Eye } from "lucide-react";
import {
    teamDashboard, teamListCollab, teamCreateCollab, teamUpdateCollab, teamDeleteCollab,
    teamListEng, teamCreateEng, teamUpdateEng, teamDeleteEng,
    teamF24MarkPaid, teamRicevutaUrl,
    teamListTemplates, teamGetTemplate, teamCreateTemplate, teamUpdateTemplate, teamDeleteTemplate,
    teamResetTemplate, teamDocPreview, teamDocRender, teamRegistroProvaPdf,
} from "@/lib/api";

const EMPTY_COLLAB = {
    nome: "", cognome: "", genere: "M", codice_fiscale: "",
    luogo_nascita: "", data_nascita: "",
    residenza_via: "", residenza_citta: "", residenza_provincia: "", residenza_cap: "",
    documento_tipo: "Carta di identità", documento_numero: "", documento_rilascio_ente: "", documento_rilascio_data: "",
    iban: "", modalita_pagamento: "bonifico", email: "", telefono: "",
    ruolo: "hostess", ruolo_custom: "", tipo_ingaggio: "diretto",
    agenzia_nome: "", agenzia_piva: "",
    patente_cat: "", patente_num: "", patente_ente: "", patente_data: "",
    note: "",
};

const EMPTY_ENG = {
    collaborator_id: "", evento: "", luogo_prestazione: "",
    data_prestazione: "", ruolo: "", descrizione_prestazione: "",
    compenso_lordo: 0, soglia_5000_superata: false, consenso_immagine: false,
    stato: "bozza", bollo_identificativo: "", bollo_data: "",
    receipt_number: "", receipt_date: "", payment_date: "",
};

const STATI = [
    { key: "bozza", label: "Bozza", color: "#9AA2AD" },
    { key: "comunicazione_inviata", label: "Comunicazione Inviata", color: "#f59e0b" },
    { key: "contratto_firmato", label: "Contratto Firmato", color: "#4B7BEA" },
    { key: "prestazione_resa", label: "Prestazione Resa", color: "#16a34a" },
    { key: "ricevuta_emessa", label: "Ricevuta Emessa", color: "#16a34a" },
    { key: "netto_pagato", label: "Netto Pagato", color: "#16a34a" },
    { key: "f24_versato", label: "F24 Versato", color: "#16a34a" },
    { key: "pronto_cu", label: "Pronto CU", color: "#16a34a" },
];

const RUOLI = [
    { v: "hostess", l: "Hostess" }, { v: "steward", l: "Steward" },
    { v: "meccanico", l: "Meccanico / Tester Tecnico" },
    { v: "altro", l: "Altro (specifica)" },
];

const MODALITA_PAG = [
    { v: "bonifico", l: "Bonifico bancario" },
    { v: "contanti", l: "Contanti" },
    { v: "assegno", l: "Assegno" },
];

export function TeamView({ token }) {
    const [sub, setSub] = useState("dashboard");
    const subs = [
        { key: "dashboard", label: "📊 Dashboard" },
        { key: "collab", label: "👥 Collaboratori" },
        { key: "eng", label: "📅 Ingaggi" },
        { key: "templates", label: "📄 Template Contratti" },
        { key: "registro", label: "🧾 Registro Prova" },
    ];
    return (
        <div className="space-y-4" data-testid="team-view">
            <div className="flex flex-wrap items-center gap-3 border-b border-[#9AA2AD]/20 pb-2">
                <Users size={20} className="text-[#E2231A]" strokeWidth={1.5} />
                <h2 className="font-display text-2xl uppercase tracking-widest text-[#F4F2ED]">Team · Collaboratori Occasionali</h2>
                <span className="text-[9px] uppercase tracking-widest text-[#9AA2AD]/60 ml-auto">MAS SRL · Sostituto d'imposta</span>
            </div>
            <div className="flex flex-wrap gap-2">
                {subs.map(s => (
                    <button key={s.key} onClick={() => setSub(s.key)}
                        className={`px-3 py-2 text-[11px] uppercase tracking-widest transition-colors ${sub === s.key ? "bg-[#E2231A] text-white" : "bg-[#1C2026] text-[#9AA2AD] hover:text-[#F4F2ED]"}`}
                        data-testid={`team-tab-${s.key}`}>
                        {s.label}
                    </button>
                ))}
            </div>
            {sub === "dashboard" && <DashboardTab token={token} />}
            {sub === "collab" && <CollabTab token={token} />}
            {sub === "eng" && <EngTab token={token} />}
            {sub === "templates" && <TemplatesTab token={token} />}
            {sub === "registro" && <RegistroProvaTab token={token} />}
        </div>
    );
}

// ═════════════════════════════ DASHBOARD ═════════════════════════════
function DashboardTab({ token }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const load = async () => {
        setLoading(true);
        try { setData(await teamDashboard(token)); } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []); // eslint-disable-line

    if (loading) return <div className="text-[#9AA2AD] p-6" data-testid="team-dashboard-loading">Caricamento…</div>;
    if (!data) return <div className="text-[#9AA2AD] p-6">Nessun dato</div>;

    const markF24Paid = async (periodo) => {
        const data_versamento = prompt("Data versamento F24 (YYYY-MM-DD)", new Date().toISOString().slice(0, 10));
        if (!data_versamento) return;
        await teamF24MarkPaid(token, periodo, data_versamento);
        load();
    };

    return (
        <div className="space-y-6" data-testid="team-dashboard">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatCard icon={<Users />} label="Collaboratori Attivi" value={data.num_collaboratori_attivi} testid="team-stat-collab" />
                <StatCard icon={<Calendar />} label="Ingaggi Anno" value={data.num_ingaggi_anno} testid="team-stat-eng" />
                <StatCard icon={<Euro />} label="Totale Lordo Anno" value={`€${data.totale_lordo_anno.toFixed(2)}`} testid="team-stat-lordo" />
                <StatCard icon={<TrendingUp />} label="Ritenute Anno (F24)" value={`€${data.totale_ritenute_anno.toFixed(2)}`} color="#E2231A" testid="team-stat-ritenute" />
            </div>

            <div className="border border-[#9AA2AD]/20 bg-[#1C2026] p-4">
                <div className="flex items-center gap-2 mb-3">
                    <AlertTriangle size={16} className="text-[#f59e0b]" strokeWidth={1.5} />
                    <h3 className="text-[11px] uppercase tracking-widest text-[#F4F2ED]">Soglia INPS Annua — €{data.soglia_inps_annuale.toFixed(0)} per collaboratore</h3>
                    <span className="ml-auto text-[9px] uppercase tracking-widest text-[#9AA2AD]">Anno {data.year}</span>
                </div>
                {data.collaboratori_soglia.length === 0 ? (
                    <div className="text-[#9AA2AD] text-sm py-3">Nessun collaboratore con ingaggi nell'anno corrente.</div>
                ) : (
                    <div className="space-y-3">
                        {data.collaboratori_soglia.map(c => (
                            <div key={c.id} data-testid={`team-soglia-row-${c.id}`}>
                                <div className="flex items-center justify-between text-xs mb-1">
                                    <span className="text-[#F4F2ED] font-medium">{c.nome}</span>
                                    <span className={c.warning_soglia ? "text-[#E2231A] font-bold" : "text-[#9AA2AD]"}>
                                        €{c.totale_lordo.toFixed(2)} / €{data.soglia_inps_annuale.toFixed(0)} · {c.soglia_5000_pct}%
                                    </span>
                                </div>
                                <div className="h-2 bg-[#15181C] overflow-hidden">
                                    <div className="h-full transition-all" style={{
                                        width: `${Math.min(100, c.soglia_5000_pct)}%`,
                                        backgroundColor: c.soglia_5000_pct >= 100 ? "#E2231A" : c.soglia_5000_pct >= 90 ? "#f59e0b" : "#16a34a"
                                    }} />
                                </div>
                                <div className="text-[9px] text-[#9AA2AD] mt-1">{c.num_ingaggi} ingaggi · Netto: €{c.totale_netto.toFixed(2)} · Ritenute: €{c.totale_ritenute.toFixed(2)}</div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="border border-[#9AA2AD]/20 bg-[#1C2026] p-4">
                <div className="flex items-center gap-2 mb-3">
                    <Euro size={16} className="text-[#E2231A]" strokeWidth={1.5} />
                    <h3 className="text-[11px] uppercase tracking-widest text-[#F4F2ED]">Scadenzario F24 — Codice tributo 1040</h3>
                    <span className="ml-auto text-[9px] uppercase tracking-widest text-[#9AA2AD]">Scadenza: 16 del mese successivo</span>
                </div>
                {data.f24_mensili.length === 0 ? (
                    <div className="text-[#9AA2AD] text-sm py-3">Nessuna ritenuta d'acconto da versare.</div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {data.f24_mensili.map(f => (
                            <div key={f.periodo} className={`border p-3 ${f.versato ? "border-[#16a34a]/40 bg-[#16a34a]/5" : "border-[#E2231A]/40 bg-[#E2231A]/5"}`} data-testid={`f24-card-${f.periodo}`}>
                                <div className="flex items-center justify-between">
                                    <div className="text-[9px] uppercase tracking-widest text-[#9AA2AD]">Periodo {f.periodo}</div>
                                    <div className={`text-[9px] uppercase tracking-widest ${f.versato ? "text-[#16a34a]" : "text-[#E2231A]"}`}>
                                        {f.versato ? "✓ Versato" : "⚠ Da versare"}
                                    </div>
                                </div>
                                <div className="font-display text-2xl text-[#F4F2ED] mt-1">€{f.importo.toFixed(2)}</div>
                                <div className="text-[10px] text-[#9AA2AD] mt-1">{f.num_ritenute} ritenute · Cod. {f.codice_tributo}</div>
                                {!f.versato && (
                                    <button onClick={() => markF24Paid(f.periodo)}
                                        className="mt-2 w-full text-[10px] uppercase tracking-widest border border-[#16a34a]/40 hover:bg-[#16a34a]/10 text-[#16a34a] px-2 py-1"
                                        data-testid={`f24-mark-paid-${f.periodo}`}>Segna come versato</button>
                                )}
                                {f.versato && f.data_versamento && (
                                    <div className="text-[10px] text-[#16a34a] mt-2">Versato il {f.data_versamento}</div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="border border-[#9AA2AD]/20 bg-[#15181C] p-4">
                <div className="flex items-center gap-2 mb-2">
                    <Building2 size={14} className="text-[#4B7BEA]" strokeWidth={1.5} />
                    <h3 className="text-[10px] uppercase tracking-widest text-[#9AA2AD]">Committente (Sostituto d'imposta)</h3>
                </div>
                <div className="text-sm text-[#F4F2ED]">{data.company.ragione_sociale} — P.IVA {data.company.piva}</div>
                <div className="text-xs text-[#9AA2AD]">{data.company.sede} · {data.company.indirizzo}</div>
                <div className="text-xs text-[#9AA2AD] mt-1">Legale Rappr.: {data.company.legale_rappresentante_nome} · Targa Prova: {data.company.targa_prova}</div>
            </div>
        </div>
    );
}

function StatCard({ icon, label, value, color, testid }) {
    return (
        <div className="border border-[#9AA2AD]/20 bg-[#1C2026] p-4" data-testid={testid}>
            <div className="flex items-center gap-2 text-[#9AA2AD]">
                <div className="[&_svg]:w-4 [&_svg]:h-4">{icon}</div>
                <div className="text-[10px] uppercase tracking-widest">{label}</div>
            </div>
            <div className="font-display text-xl mt-2" style={{ color: color || "#F4F2ED" }}>{value}</div>
        </div>
    );
}

// ═════════════════════════════ COLLABORATORI ═════════════════════════════
function CollabTab({ token }) {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [genDoc, setGenDoc] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try { const r = await teamListCollab(token); setItems(r.items || []); } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []); // eslint-disable-line

    const remove = async (id) => {
        if (!confirm("Cancellare questo collaboratore? Se ha ingaggi collegati, dovrai cancellarli prima.")) return;
        try { await teamDeleteCollab(token, id); load(); }
        catch (e) { alert(e.response?.data?.detail || "Errore"); }
    };

    return (
        <div className="space-y-3" data-testid="team-collab-tab">
            <div className="flex items-center justify-between">
                <div className="text-[11px] uppercase tracking-widest text-[#9AA2AD]">{items.length} collaboratori</div>
                <button onClick={() => setEditing({ ...EMPTY_COLLAB })}
                    className="bg-[#E2231A] hover:bg-[#c11e15] text-white px-3 py-2 text-[11px] uppercase tracking-widest flex items-center gap-1"
                    data-testid="btn-add-collab">
                    <Plus size={14} /> Nuovo Collaboratore
                </button>
            </div>

            {loading ? <div className="text-[#9AA2AD] p-6">Caricamento…</div> : (
                <div className="border border-[#9AA2AD]/20 bg-[#15181C] overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-[#1C2026] text-[10px] uppercase tracking-widest text-[#9AA2AD]">
                            <tr>
                                <th className="text-left p-3">Nome</th>
                                <th className="text-left p-3">Codice Fiscale</th>
                                <th className="text-left p-3">Ruolo</th>
                                <th className="text-left p-3">Tipo</th>
                                <th className="text-left p-3">Pagamento</th>
                                <th className="text-right p-3">Azioni</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.length === 0 ? (
                                <tr><td colSpan={6} className="p-6 text-center text-[#9AA2AD]">Nessun collaboratore. Aggiungi il primo con il bottone in alto a destra.</td></tr>
                            ) : items.map(c => (
                                <tr key={c.id} className="border-t border-[#9AA2AD]/10 hover:bg-[#1C2026]/40" data-testid={`collab-row-${c.id}`}>
                                    <td className="p-3">
                                        <div className="text-[#F4F2ED] font-medium">{c.cognome} {c.nome}</div>
                                        <div className="text-[10px] text-[#9AA2AD]">{c.residenza_citta} ({c.residenza_provincia})</div>
                                    </td>
                                    <td className="p-3 font-mono text-xs text-[#9AA2AD]">{c.codice_fiscale}</td>
                                    <td className="p-3 text-xs">
                                        {c.ruolo === "altro" && c.ruolo_custom ? `${c.ruolo_custom}` : c.ruolo}
                                    </td>
                                    <td className="p-3 text-xs">
                                        {c.tipo_ingaggio === "agenzia" ? (
                                            <span className="text-[#4B7BEA]">🏢 Agenzia<div className="text-[9px] text-[#9AA2AD]">{c.agenzia_nome}</div></span>
                                        ) : <span className="text-[#16a34a]">🤝 Diretto</span>}
                                    </td>
                                    <td className="p-3 text-xs">
                                        <div className="text-[#F4F2ED]">{MODALITA_PAG.find(m => m.v === c.modalita_pagamento)?.l || c.modalita_pagamento}</div>
                                        {c.iban && <div className="text-[9px] font-mono text-[#9AA2AD]">{c.iban.slice(0, 15)}…</div>}
                                    </td>
                                    <td className="p-3 text-right">
                                        <div className="flex justify-end gap-1 flex-wrap">
                                            <button onClick={() => setGenDoc(c)}
                                                className="text-[10px] uppercase tracking-widest border border-[#E2231A]/40 bg-[#E2231A]/10 hover:bg-[#E2231A]/20 text-[#E2231A] px-2 py-1 flex items-center gap-1"
                                                data-testid={`collab-genera-${c.id}`}>
                                                <FileSignature size={11} /> Genera Documento
                                            </button>
                                            <button onClick={() => setEditing({ ...c })}
                                                className="text-[10px] uppercase tracking-widest border border-[#9AA2AD]/40 hover:bg-[#9AA2AD]/10 text-[#9AA2AD] px-2 py-1 flex items-center gap-1"
                                                data-testid={`collab-edit-${c.id}`}>
                                                <Edit3 size={11} /> Modifica
                                            </button>
                                            <button onClick={() => remove(c.id)}
                                                className="text-[10px] uppercase tracking-widest border border-[#E2231A]/40 hover:bg-[#E2231A]/10 text-[#E2231A] px-2 py-1"
                                                data-testid={`collab-delete-${c.id}`}>
                                                <Trash2 size={11} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {editing && <CollabModal token={token} initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
            {genDoc && <GenerateDocModal token={token} collab={genDoc} onClose={() => setGenDoc(null)} />}
        </div>
    );
}

function CollabModal({ token, initial, onClose, onSaved }) {
    const [f, setF] = useState(initial);
    const [busy, setBusy] = useState(false);
    const isNew = !initial.id;
    const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));

    const save = async () => {
        if (!f.nome || !f.cognome) { alert("Nome e Cognome sono obbligatori"); return; }
        setBusy(true);
        try {
            if (isNew) await teamCreateCollab(token, f);
            else await teamUpdateCollab(token, f.id, f);
            onSaved();
        } catch (e) { alert(e.response?.data?.detail || "Errore salvataggio"); }
        finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="collab-modal">
            <div className="bg-[#15181C] border border-[#9AA2AD]/30 max-w-3xl w-full max-h-[92vh] overflow-y-auto">
                <div className="sticky top-0 bg-[#1C2026] border-b border-[#9AA2AD]/20 p-4 flex items-center justify-between z-10">
                    <h3 className="font-display text-lg text-[#F4F2ED]">{isNew ? "Nuovo Collaboratore" : "Modifica Collaboratore"}</h3>
                    <button onClick={onClose} className="text-[#9AA2AD] hover:text-[#F4F2ED]"><X size={18} /></button>
                </div>
                <div className="p-4 space-y-4">
                    <Section title="Anagrafica">
                        <Field label="Nome *" value={f.nome} onChange={v => set("nome", v)} testid="col-nome" />
                        <Field label="Cognome *" value={f.cognome} onChange={v => set("cognome", v)} testid="col-cognome" />
                        <Field label="Genere" select options={[{ v: "M", l: "M · Sig." }, { v: "F", l: "F · Sig.ra" }]} value={f.genere} onChange={v => set("genere", v)} testid="col-genere" />
                        <Field label="Codice Fiscale" value={f.codice_fiscale} onChange={v => set("codice_fiscale", v.toUpperCase())} mono testid="col-cf" />
                        <Field label="Luogo di nascita" value={f.luogo_nascita} onChange={v => set("luogo_nascita", v)} placeholder="Nola (NA)" testid="col-nato-a" />
                        <Field label="Data di nascita" type="date" value={f.data_nascita} onChange={v => set("data_nascita", v)} testid="col-nato-il" />
                        <Field label="Ruolo" select options={RUOLI} value={f.ruolo} onChange={v => set("ruolo", v)} testid="col-ruolo" />
                        {f.ruolo === "altro" && (
                            <Field label="Ruolo (specifica)" value={f.ruolo_custom} onChange={v => set("ruolo_custom", v)} placeholder="Es. modella, DJ, fotografo…" testid="col-ruolo-custom" />
                        )}
                    </Section>

                    <Section title="Residenza">
                        <Field label="Via/Piazza" value={f.residenza_via} onChange={v => set("residenza_via", v)} colSpan={2} testid="col-via" />
                        <Field label="Città" value={f.residenza_citta} onChange={v => set("residenza_citta", v)} testid="col-citta" />
                        <Field label="Provincia" value={f.residenza_provincia} onChange={v => set("residenza_provincia", v.toUpperCase())} maxLength={2} testid="col-prov" />
                        <Field label="CAP" value={f.residenza_cap} onChange={v => set("residenza_cap", v)} testid="col-cap" />
                    </Section>

                    <Section title="Documento identità">
                        <Field label="Tipo" value={f.documento_tipo} onChange={v => set("documento_tipo", v)} testid="col-doc-tipo" />
                        <Field label="Numero" value={f.documento_numero} onChange={v => set("documento_numero", v)} mono testid="col-doc-num" />
                        <Field label="Rilasciato dal" value={f.documento_rilascio_ente} onChange={v => set("documento_rilascio_ente", v)} placeholder="Comune di Marigliano" testid="col-doc-ente" />
                        <Field label="Data rilascio" type="date" value={f.documento_rilascio_data} onChange={v => set("documento_rilascio_data", v)} testid="col-doc-data" />
                    </Section>

                    {(f.ruolo === "meccanico" || f.patente_num) && (
                        <Section title="Patente (per Meccanico / Delega Targa)">
                            <Field label="Categoria" value={f.patente_cat} onChange={v => set("patente_cat", v.toUpperCase())} placeholder="B" testid="col-pat-cat" />
                            <Field label="Numero" value={f.patente_num} onChange={v => set("patente_num", v)} mono testid="col-pat-num" />
                            <Field label="Rilasciato dal" value={f.patente_ente} onChange={v => set("patente_ente", v)} placeholder="MCTC / Comune" testid="col-pat-ente" />
                            <Field label="Data rilascio" type="date" value={f.patente_data} onChange={v => set("patente_data", v)} testid="col-pat-data" />
                        </Section>
                    )}

                    <Section title="Contatti & Pagamento">
                        <Field label="Email" type="email" value={f.email} onChange={v => set("email", v)} testid="col-email" />
                        <Field label="Telefono" value={f.telefono} onChange={v => set("telefono", v)} testid="col-tel" />
                        <Field label="Modalità pagamento" select options={MODALITA_PAG} value={f.modalita_pagamento} onChange={v => set("modalita_pagamento", v)} testid="col-mod-pag" />
                        <Field label="IBAN (se bonifico)" value={f.iban} onChange={v => set("iban", v.toUpperCase())} mono testid="col-iban" />
                    </Section>

                    <Section title="Tipologia Ingaggio">
                        <Field label="Modalità" select
                            options={[{ v: "diretto", l: "🤝 Diretto (con MAS SRL)" }, { v: "agenzia", l: "🏢 Tramite Agenzia" }]}
                            value={f.tipo_ingaggio} onChange={v => set("tipo_ingaggio", v)} colSpan={2} testid="col-tipo-ing" />
                        {f.tipo_ingaggio === "agenzia" && (
                            <>
                                <Field label="Nome Agenzia" value={f.agenzia_nome} onChange={v => set("agenzia_nome", v)} testid="col-ag-nome" />
                                <Field label="P.IVA Agenzia" value={f.agenzia_piva} onChange={v => set("agenzia_piva", v)} mono testid="col-ag-piva" />
                            </>
                        )}
                    </Section>

                    <Section title="Note">
                        <Field label="Note interne" textarea value={f.note} onChange={v => set("note", v)} colSpan={2} testid="col-note" />
                    </Section>
                </div>
                <div className="sticky bottom-0 bg-[#1C2026] border-t border-[#9AA2AD]/20 p-3 flex justify-end gap-2">
                    <button onClick={onClose} className="border border-[#9AA2AD]/40 text-[#9AA2AD] px-4 py-2 text-[11px] uppercase tracking-widest" data-testid="col-cancel">Annulla</button>
                    <button onClick={save} disabled={busy} className="bg-[#E2231A] disabled:opacity-50 text-white px-4 py-2 text-[11px] uppercase tracking-widest" data-testid="col-save">
                        {busy ? "Salvataggio…" : (isNew ? "Crea" : "Salva")}
                    </button>
                </div>
            </div>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-widest text-[#9AA2AD] mb-2 border-b border-[#9AA2AD]/10 pb-1">{title}</div>
            <div className="grid grid-cols-2 gap-3">{children}</div>
        </div>
    );
}

function Field({ label, value, onChange, type = "text", mono, colSpan = 1, placeholder, textarea, select, options, maxLength, testid, rows = 3 }) {
    const cls = `w-full bg-[#0F1114] border border-[#9AA2AD]/20 text-[#F4F2ED] px-3 py-2 text-sm ${mono ? "font-mono" : ""}`;
    return (
        <div className={colSpan === 2 ? "col-span-2" : ""}>
            <label className="block text-[9px] uppercase tracking-widest text-[#9AA2AD] mb-1">{label}</label>
            {textarea ? (
                <textarea rows={rows} className={cls} value={value || ""} onChange={e => onChange(e.target.value)} placeholder={placeholder} data-testid={testid} />
            ) : select ? (
                <select className={cls} value={value || ""} onChange={e => onChange(e.target.value)} data-testid={testid}>
                    {options.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                </select>
            ) : (
                <input type={type} className={cls} value={value || ""} onChange={e => onChange(e.target.value)} placeholder={placeholder} maxLength={maxLength} data-testid={testid} />
            )}
        </div>
    );
}

// ═════════════════════════════ INGAGGI ═════════════════════════════
function EngTab({ token }) {
    const [items, setItems] = useState([]);
    const [collabs, setCollabs] = useState([]);
    const [editing, setEditing] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const [er, cr] = await Promise.all([teamListEng(token), teamListCollab(token)]);
            setItems(er.items || []); setCollabs(cr.items || []);
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []); // eslint-disable-line

    const remove = async (id) => {
        if (!confirm("Cancellare questo ingaggio?")) return;
        await teamDeleteEng(token, id); load();
    };

    if (loading) return <div className="text-[#9AA2AD] p-6">Caricamento…</div>;

    return (
        <div className="space-y-3" data-testid="team-eng-tab">
            <div className="flex items-center justify-between">
                <div className="text-[11px] uppercase tracking-widest text-[#9AA2AD]">{items.length} ingaggi</div>
                <button onClick={() => setEditing({ ...EMPTY_ENG })}
                    disabled={collabs.length === 0}
                    className="bg-[#E2231A] hover:bg-[#c11e15] disabled:opacity-40 text-white px-3 py-2 text-[11px] uppercase tracking-widest flex items-center gap-1"
                    data-testid="btn-add-eng">
                    <Plus size={14} /> Nuovo Ingaggio
                </button>
            </div>
            {collabs.length === 0 && (
                <div className="border border-[#f59e0b]/40 bg-[#f59e0b]/5 p-3 text-[#f59e0b] text-xs">
                    ⚠️ Devi prima creare almeno un collaboratore.
                </div>
            )}

            <div className="border border-[#9AA2AD]/20 bg-[#15181C] overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-[#1C2026] text-[10px] uppercase tracking-widest text-[#9AA2AD]">
                        <tr>
                            <th className="text-left p-3">Data · Evento</th>
                            <th className="text-left p-3">Collaboratore</th>
                            <th className="text-left p-3">Lordo</th>
                            <th className="text-left p-3">Ritenuta</th>
                            <th className="text-left p-3">Bollo</th>
                            <th className="text-left p-3">Netto</th>
                            <th className="text-left p-3">Stato</th>
                            <th className="text-right p-3">Azioni</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.length === 0 ? (
                            <tr><td colSpan={8} className="p-6 text-center text-[#9AA2AD]">Nessun ingaggio ancora inserito.</td></tr>
                        ) : items.map(e => {
                            const st = STATI.find(s => s.key === e.stato) || STATI[0];
                            return (
                                <tr key={e.id} className="border-t border-[#9AA2AD]/10 hover:bg-[#1C2026]/40" data-testid={`eng-row-${e.id}`}>
                                    <td className="p-3">
                                        <div className="text-[#F4F2ED]">{e.data_prestazione || "—"}</div>
                                        <div className="text-[10px] text-[#9AA2AD]">{e.evento || "(evento non specificato)"}</div>
                                    </td>
                                    <td className="p-3 text-xs">
                                        <div className="text-[#F4F2ED]">{e.collaborator_name}</div>
                                        <div className="text-[9px] text-[#9AA2AD]">{e.ruolo}</div>
                                    </td>
                                    <td className="p-3 text-sm">€{e.calcoli?.compenso_lordo?.toFixed(2)}</td>
                                    <td className="p-3 text-sm text-[#E2231A]">−€{e.calcoli?.ritenuta_acconto?.toFixed(2)}</td>
                                    <td className="p-3 text-sm text-[#f59e0b]">{e.calcoli?.bollo_dovuto > 0 ? `€${e.calcoli.bollo_dovuto.toFixed(2)}` : "—"}</td>
                                    <td className="p-3 text-sm text-[#16a34a] font-medium">€{e.calcoli?.netto_a_pagare?.toFixed(2)}</td>
                                    <td className="p-3">
                                        <span className="text-[9px] uppercase tracking-widest px-2 py-1" style={{ color: st.color, backgroundColor: `${st.color}20` }}>{st.label}</span>
                                    </td>
                                    <td className="p-3 text-right">
                                        <div className="flex justify-end gap-1 flex-wrap">
                                            <a href={teamRicevutaUrl(token, e.id)} target="_blank" rel="noreferrer"
                                                className="text-[10px] uppercase tracking-widest border border-[#16a34a]/40 hover:bg-[#16a34a]/10 text-[#16a34a] px-2 py-1 flex items-center gap-1"
                                                data-testid={`eng-receipt-${e.id}`}>
                                                <Download size={11} /> Ricevuta
                                            </a>
                                            <button onClick={() => setEditing({ ...e })}
                                                className="text-[10px] uppercase tracking-widest border border-[#9AA2AD]/40 hover:bg-[#9AA2AD]/10 text-[#9AA2AD] px-2 py-1"
                                                data-testid={`eng-edit-${e.id}`}>
                                                <Edit3 size={11} />
                                            </button>
                                            <button onClick={() => remove(e.id)}
                                                className="text-[10px] uppercase tracking-widest border border-[#E2231A]/40 hover:bg-[#E2231A]/10 text-[#E2231A] px-2 py-1"
                                                data-testid={`eng-delete-${e.id}`}>
                                                <Trash2 size={11} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {editing && <EngModal token={token} initial={editing} collabs={collabs} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
        </div>
    );
}

function EngModal({ token, initial, collabs, onClose, onSaved }) {
    const [f, setF] = useState(initial);
    const [busy, setBusy] = useState(false);
    const [warning, setWarning] = useState(null);
    const isNew = !initial.id;
    const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));

    const lordo = parseFloat(f.compenso_lordo) || 0;
    const ritenuta = Math.round(lordo * 20) / 100;
    const bollo = lordo > 77.47 ? 2.00 : 0;
    const netto = Math.round((lordo - ritenuta) * 100) / 100;

    const save = async () => {
        if (!f.collaborator_id) { alert("Seleziona un collaboratore"); return; }
        if (!f.data_prestazione) { alert("Data prestazione obbligatoria"); return; }
        setBusy(true);
        try {
            const r = isNew ? await teamCreateEng(token, f) : await teamUpdateEng(token, f.id, f);
            if (r.warning) { setWarning(r.warning); setBusy(false); return; }
            onSaved();
        } catch (e) { alert(e.response?.data?.detail || "Errore"); }
        finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="eng-modal">
            <div className="bg-[#15181C] border border-[#9AA2AD]/30 max-w-3xl w-full max-h-[92vh] overflow-y-auto">
                <div className="sticky top-0 bg-[#1C2026] border-b border-[#9AA2AD]/20 p-4 flex items-center justify-between z-10">
                    <h3 className="font-display text-lg text-[#F4F2ED]">{isNew ? "Nuovo Ingaggio" : "Modifica Ingaggio"}</h3>
                    <button onClick={onClose} className="text-[#9AA2AD] hover:text-[#F4F2ED]"><X size={18} /></button>
                </div>
                <div className="p-4 space-y-4">
                    <Section title="Ingaggio">
                        <Field label="Collaboratore *" select
                            options={[{ v: "", l: "— Seleziona —" }, ...collabs.map(c => ({ v: c.id, l: `${c.cognome} ${c.nome} (${c.ruolo})` }))]}
                            value={f.collaborator_id} onChange={v => set("collaborator_id", v)} colSpan={2} testid="eng-collab" />
                        <Field label="Evento" value={f.evento} onChange={v => set("evento", v)} placeholder="VELORA JDM @ Modena 2026" testid="eng-evento" />
                        <Field label="Luogo prestazione" value={f.luogo_prestazione} onChange={v => set("luogo_prestazione", v)} placeholder="Autodromo di Modena" testid="eng-luogo" />
                        <Field label="Data prestazione *" type="date" value={f.data_prestazione} onChange={v => set("data_prestazione", v)} testid="eng-data" />
                        <Field label="Ruolo" value={f.ruolo} onChange={v => set("ruolo", v)} placeholder="hostess / tecnico / …" testid="eng-ruolo" />
                        <Field label="Descrizione prestazione" textarea value={f.descrizione_prestazione} onChange={v => set("descrizione_prestazione", v)} colSpan={2} testid="eng-descr" />
                    </Section>

                    <Section title="Compenso & Calcoli automatici">
                        <Field label="Compenso lordo (€) *" type="number" value={f.compenso_lordo} onChange={v => set("compenso_lordo", v)} testid="eng-lordo" />
                        <Field label="Data pagamento (per F24)" type="date" value={f.payment_date} onChange={v => set("payment_date", v)} testid="eng-pay-date" />
                        <div className="col-span-2 grid grid-cols-4 gap-2 bg-[#0F1114] border border-[#9AA2AD]/20 p-3">
                            <MiniCalc label="Lordo" value={`€${lordo.toFixed(2)}`} />
                            <MiniCalc label="Ritenuta 20%" value={`−€${ritenuta.toFixed(2)}`} color="#E2231A" />
                            <MiniCalc label="Bollo" value={bollo > 0 ? `€${bollo.toFixed(2)}` : "—"} color="#f59e0b" />
                            <MiniCalc label="Netto" value={`€${netto.toFixed(2)}`} color="#16a34a" bold />
                        </div>
                        <div className="col-span-2 flex items-center gap-3 py-2">
                            <label className="flex items-center gap-2 text-xs text-[#F4F2ED]">
                                <input type="checkbox" checked={f.soglia_5000_superata} onChange={e => set("soglia_5000_superata", e.target.checked)} data-testid="eng-soglia" />
                                Soglia €5.000 annua SUPERATA (attiva alert INPS)
                            </label>
                            <label className="flex items-center gap-2 text-xs text-[#F4F2ED]">
                                <input type="checkbox" checked={f.consenso_immagine} onChange={e => set("consenso_immagine", e.target.checked)} data-testid="eng-immagine" />
                                Consenso uso immagine
                            </label>
                        </div>
                    </Section>

                    {bollo > 0 && (
                        <Section title="Marca da bollo (obbligatoria)">
                            <Field label="Identificativo contrassegno (16 cifre)" value={f.bollo_identificativo} onChange={v => set("bollo_identificativo", v)} mono testid="eng-bollo-id" />
                            <Field label="Data contrassegno" type="date" value={f.bollo_data} onChange={v => set("bollo_data", v)} testid="eng-bollo-data" />
                        </Section>
                    )}

                    <Section title="Stato ingaggio">
                        <Field label="Stato attuale" select options={STATI.map(s => ({ v: s.key, l: s.label }))}
                            value={f.stato} onChange={v => set("stato", v)} colSpan={2} testid="eng-stato" />
                    </Section>

                    {warning && <div className="border border-[#E2231A]/40 bg-[#E2231A]/10 p-3 text-[#E2231A] text-xs" data-testid="eng-warning">{warning}</div>}
                </div>
                <div className="sticky bottom-0 bg-[#1C2026] border-t border-[#9AA2AD]/20 p-3 flex justify-end gap-2">
                    <button onClick={onClose} className="border border-[#9AA2AD]/40 text-[#9AA2AD] px-4 py-2 text-[11px] uppercase tracking-widest" data-testid="eng-cancel">Annulla</button>
                    <button onClick={save} disabled={busy} className="bg-[#E2231A] disabled:opacity-50 text-white px-4 py-2 text-[11px] uppercase tracking-widest" data-testid="eng-save">
                        {busy ? "Salvataggio…" : (isNew ? "Crea" : "Salva")}
                    </button>
                </div>
            </div>
        </div>
    );
}

function MiniCalc({ label, value, color, bold }) {
    return (
        <div className="text-center">
            <div className="text-[9px] uppercase tracking-widest text-[#9AA2AD]">{label}</div>
            <div className={`font-display text-lg mt-1 ${bold ? "font-bold" : ""}`} style={{ color: color || "#F4F2ED" }}>{value}</div>
        </div>
    );
}

// ═════════════════════════════ TEMPLATE ═════════════════════════════
function TemplatesTab({ token }) {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try { const r = await teamListTemplates(token); setItems(r.items || []); } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []); // eslint-disable-line

    const remove = async (t) => {
        if (t.is_default) { alert("I template ufficiali MAS SRL non si possono cancellare. Puoi solo modificarli o ripristinarli."); return; }
        if (!confirm(`Cancellare "${t.titolo}"?`)) return;
        try { await teamDeleteTemplate(token, t.id); load(); }
        catch (e) { alert(e.response?.data?.detail || "Errore"); }
    };

    const reset = async (t) => {
        if (!confirm(`Ripristinare "${t.titolo}" al testo ufficiale MAS SRL originale? Le tue modifiche verranno perse.`)) return;
        try { await teamResetTemplate(token, t.id); load(); }
        catch (e) { alert(e.response?.data?.detail || "Errore"); }
    };

    return (
        <div className="space-y-3" data-testid="team-templates-tab">
            <div className="border border-[#4B7BEA]/30 bg-[#4B7BEA]/5 p-3 text-xs text-[#F4F2ED]">
                📋 Qui puoi <strong>modificare il testo</strong> dei contratti/deleghe ufficiali MAS SRL, salvarli o crearne di nuovi.
                Ogni template usa <strong>placeholder</strong> tipo <code className="text-[#4B7BEA]">{"{{NOME_COMPLETO}}"}</code>, <code className="text-[#4B7BEA]">{"{{CF}}"}</code>, <code className="text-[#4B7BEA]">{"{{COMPENSO_LORDO}}"}</code>… che vengono sostituiti automaticamente in fase di generazione PDF.
                Se pasticciate accidentalmente un template ufficiale, cliccate <RotateCcw size={11} className="inline" /> Ripristina per riportarlo al testo originale.
            </div>
            <div className="flex items-center justify-between">
                <div className="text-[11px] uppercase tracking-widest text-[#9AA2AD]">{items.length} template</div>
                <button onClick={() => setEditing({ key: "", categoria: "Contratto", titolo: "", body: "", genere: "M" })}
                    className="bg-[#E2231A] hover:bg-[#c11e15] text-white px-3 py-2 text-[11px] uppercase tracking-widest flex items-center gap-1"
                    data-testid="btn-add-template">
                    <Plus size={14} /> Nuovo Template
                </button>
            </div>

            {loading ? <div className="text-[#9AA2AD] p-6">Caricamento…</div> : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {items.map(t => (
                        <div key={t.id} className="border border-[#9AA2AD]/20 bg-[#1C2026] p-3" data-testid={`tpl-card-${t.key}`}>
                            <div className="flex items-start justify-between gap-2">
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="text-[9px] uppercase tracking-widest px-2 py-0.5 bg-[#4B7BEA]/20 text-[#4B7BEA]">{t.categoria}</span>
                                        {t.is_default && <span className="text-[9px] uppercase tracking-widest px-2 py-0.5 bg-[#16a34a]/20 text-[#16a34a]">Ufficiale MAS</span>}
                                    </div>
                                    <div className="text-sm text-[#F4F2ED] font-medium">{t.titolo}</div>
                                    <div className="text-[10px] text-[#9AA2AD] mt-1 font-mono">key: {t.key}</div>
                                </div>
                            </div>
                            <div className="mt-3 flex gap-2">
                                <button onClick={() => setEditing({ ...t })}
                                    className="flex-1 border border-[#9AA2AD]/40 hover:bg-[#9AA2AD]/10 text-[#F4F2ED] text-[10px] uppercase tracking-widest px-3 py-2 flex items-center justify-center gap-1"
                                    data-testid={`tpl-edit-${t.key}`}>
                                    <Edit3 size={11} /> Modifica
                                </button>
                                {t.is_default ? (
                                    <button onClick={() => reset(t)}
                                        className="border border-[#f59e0b]/40 hover:bg-[#f59e0b]/10 text-[#f59e0b] text-[10px] uppercase tracking-widest px-3 py-2 flex items-center gap-1"
                                        title="Ripristina testo ufficiale"
                                        data-testid={`tpl-reset-${t.key}`}>
                                        <RotateCcw size={11} />
                                    </button>
                                ) : (
                                    <button onClick={() => remove(t)}
                                        className="border border-[#E2231A]/40 hover:bg-[#E2231A]/10 text-[#E2231A] text-[10px] uppercase tracking-widest px-3 py-2 flex items-center gap-1"
                                        data-testid={`tpl-delete-${t.key}`}>
                                        <Trash2 size={11} />
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {editing && <TemplateModal token={token} initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
        </div>
    );
}

const PLACEHOLDERS = [
    "RAGIONE_SOCIALE", "SEDE_LEGALE", "PIVA", "LEGALE_RAPPR", "LEGALE_RAPPR_CF",
    "TARGA_PROVA", "TARGA_PROVA_AUTORIZZAZIONE", "TARGA_PROVA_MC_CITTA", "TARGA_PROVA_DATA_RILASCIO", "FORO",
    "NOME", "COGNOME", "NOME_COMPLETO", "CF", "LUOGO_NASCITA", "DATA_NASCITA", "RESIDENZA",
    "DOC_TIPO", "DOC_NUMERO", "DOC_TIPO_NUMERO", "DOC_ENTE", "DOC_DATA",
    "EMAIL", "TELEFONO", "IBAN", "MODALITA_PAGAMENTO",
    "PATENTE_CAT", "PATENTE_NUM", "PATENTE_ENTE", "PATENTE_DATA",
    "RUOLO", "RUOLO_CUSTOM",
    "EVENTO", "LUOGO_EVENTO", "DATA_EVENTO",
    "COMPENSO_LORDO", "COMPENSO_NETTO", "COMPENSO_LETTERE", "GIORNI_PAGAMENTO", "DESCRIZIONE_PRESTAZIONE",
    "RIF_CONTRATTO", "DATA_CONTRATTO",
    "SOGLIA_INPS", "RCT_ESTESA", "IMMAGINE_CONSENSO", "RECESSO_RIMBORSO", "VALIDITA_TARGA",
    "LUOGO_FIRMA", "DATA_OGGI", "ANNO",
];

function TemplateModal({ token, initial, onClose, onSaved }) {
    const [f, setF] = useState(initial);
    const [busy, setBusy] = useState(false);
    const isNew = !initial.id;
    const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));

    const insertPlaceholder = (ph) => {
        const el = document.getElementById("tpl-body-textarea");
        if (!el) return;
        const start = el.selectionStart, end = el.selectionEnd;
        const before = f.body.substring(0, start);
        const after = f.body.substring(end);
        const newBody = `${before}{{${ph}}}${after}`;
        setF(prev => ({ ...prev, body: newBody }));
        setTimeout(() => { el.focus(); el.setSelectionRange(start + ph.length + 4, start + ph.length + 4); }, 0);
    };

    const save = async () => {
        if (!f.titolo || !f.body) { alert("Titolo e testo sono obbligatori"); return; }
        setBusy(true);
        try {
            if (isNew) await teamCreateTemplate(token, f);
            else await teamUpdateTemplate(token, f.id, f);
            onSaved();
        } catch (e) { alert(e.response?.data?.detail || "Errore"); }
        finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="tpl-modal">
            <div className="bg-[#15181C] border border-[#9AA2AD]/30 max-w-5xl w-full max-h-[95vh] overflow-y-auto">
                <div className="sticky top-0 bg-[#1C2026] border-b border-[#9AA2AD]/20 p-4 flex items-center justify-between z-10">
                    <h3 className="font-display text-lg text-[#F4F2ED]">{isNew ? "Nuovo Template" : `Modifica: ${f.titolo}`}</h3>
                    <button onClick={onClose} className="text-[#9AA2AD] hover:text-[#F4F2ED]"><X size={18} /></button>
                </div>
                <div className="p-4 grid grid-cols-1 lg:grid-cols-4 gap-4">
                    <div className="lg:col-span-3 space-y-3">
                        <div className="grid grid-cols-3 gap-3">
                            <Field label="Titolo *" value={f.titolo} onChange={v => set("titolo", v)} colSpan={2} testid="tpl-titolo" />
                            <Field label="Categoria" select options={[{ v: "Contratto", l: "Contratto" }, { v: "Delega", l: "Delega" }, { v: "Altro", l: "Altro" }]} value={f.categoria} onChange={v => set("categoria", v)} testid="tpl-cat" />
                        </div>
                        {isNew && (
                            <Field label="Key (id univoco lower_snake_case)" value={f.key} onChange={v => set("key", v.toLowerCase().replace(/[^a-z0-9_]/g, "_"))} placeholder="es. contratto_dj" mono testid="tpl-key" />
                        )}
                        <div>
                            <div className="flex items-center justify-between mb-1">
                                <label className="text-[9px] uppercase tracking-widest text-[#9AA2AD]">Testo template (usa {"{{PLACEHOLDER}}"} dalla lista →)</label>
                                <div className="text-[9px] text-[#9AA2AD]">{f.body.length} caratteri</div>
                            </div>
                            <textarea id="tpl-body-textarea" rows={26}
                                className="w-full bg-[#0F1114] border border-[#9AA2AD]/20 text-[#F4F2ED] px-3 py-2 text-xs font-mono leading-relaxed"
                                value={f.body || ""} onChange={e => set("body", e.target.value)}
                                data-testid="tpl-body" />
                        </div>
                    </div>
                    <div className="lg:col-span-1">
                        <div className="text-[9px] uppercase tracking-widest text-[#9AA2AD] mb-2">Inserisci Placeholder</div>
                        <div className="border border-[#9AA2AD]/20 bg-[#0F1114] p-2 max-h-[600px] overflow-y-auto space-y-1">
                            {PLACEHOLDERS.map(ph => (
                                <button key={ph} onClick={() => insertPlaceholder(ph)}
                                    className="w-full text-left px-2 py-1 text-[10px] font-mono text-[#4B7BEA] hover:bg-[#4B7BEA]/10 border border-transparent hover:border-[#4B7BEA]/30"
                                    data-testid={`tpl-ph-${ph}`}>
                                    {`{{${ph}}}`}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
                <div className="sticky bottom-0 bg-[#1C2026] border-t border-[#9AA2AD]/20 p-3 flex justify-end gap-2">
                    <button onClick={onClose} className="border border-[#9AA2AD]/40 text-[#9AA2AD] px-4 py-2 text-[11px] uppercase tracking-widest" data-testid="tpl-cancel">Annulla</button>
                    <button onClick={save} disabled={busy} className="bg-[#E2231A] disabled:opacity-50 text-white px-4 py-2 text-[11px] uppercase tracking-widest" data-testid="tpl-save">
                        {busy ? "Salvataggio…" : (isNew ? "Crea" : "Salva Modifiche")}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ═════════════════════════════ GENERATE DOCUMENT MODAL ═════════════════════════════
function GenerateDocModal({ token, collab, onClose }) {
    const [templates, setTemplates] = useState([]);
    const [engagements, setEngagements] = useState([]);
    const [selectedTpl, setSelectedTpl] = useState("");
    const [selectedEng, setSelectedEng] = useState("");
    const [extra, setExtra] = useState({
        luogo_firma: "",
        giorni_pagamento: "30",
        recesso_rimborso: "nulla",
        rct_estesa: "di aver esteso",
        validita_targa: "",
        rif_contratto: "",
        data_contratto: "",
    });
    const [preview, setPreview] = useState("");
    const [loading, setLoading] = useState(true);
    const [busyPdf, setBusyPdf] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const [tr, er] = await Promise.all([
                    teamListTemplates(token),
                    teamListEng(token, collab.id),
                ]);
                setTemplates(tr.items || []);
                setEngagements(er.items || []);
                // Default template = quello che matcha il ruolo
                const key = collab.ruolo === "meccanico" ? "contratto_meccanico" :
                            collab.ruolo === "hostess" ? "contratto_hostess" :
                            collab.ruolo === "steward" ? "contratto_steward" :
                            "contratto_altro";
                const def = (tr.items || []).find(t => t.key === key);
                if (def) setSelectedTpl(def.id);
                // Default engagement = più recente
                if (er.items && er.items[0]) setSelectedEng(er.items[0].id);
            } finally { setLoading(false); }
        })();
    }, [collab.id, token]);

    const selectedTplObj = templates.find(t => t.id === selectedTpl);
    const isDelega = selectedTplObj?.categoria === "Delega";

    // Preview live
    useEffect(() => {
        if (!selectedTpl) return;
        (async () => {
            try {
                const r = await teamDocPreview(token, {
                    template_id: selectedTpl,
                    collaborator_id: collab.id,
                    engagement_id: selectedEng || null,
                    extra,
                });
                setPreview(r.resolved_text);
            } catch (e) { setPreview("Errore preview"); }
        })();
    }, [selectedTpl, selectedEng, extra, collab.id, token]);

    const download = async () => {
        setBusyPdf(true);
        try {
            const blob = await teamDocRender(token, {
                template_id: selectedTpl,
                collaborator_id: collab.id,
                engagement_id: selectedEng || null,
                extra,
            });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${selectedTplObj?.key || "doc"}_${collab.cognome}_${collab.nome}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) { alert(e.response?.data?.detail || "Errore generazione PDF"); }
        finally { setBusyPdf(false); }
    };

    return (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="gen-doc-modal">
            <div className="bg-[#15181C] border border-[#9AA2AD]/30 max-w-6xl w-full max-h-[95vh] overflow-y-auto">
                <div className="sticky top-0 bg-[#1C2026] border-b border-[#9AA2AD]/20 p-4 flex items-center justify-between z-10">
                    <div>
                        <h3 className="font-display text-lg text-[#F4F2ED]">Genera Documento — {collab.cognome} {collab.nome}</h3>
                        <div className="text-[10px] text-[#9AA2AD] mt-1">CF {collab.codice_fiscale} · {collab.ruolo === "altro" ? collab.ruolo_custom : collab.ruolo}</div>
                    </div>
                    <button onClick={onClose} className="text-[#9AA2AD] hover:text-[#F4F2ED]"><X size={18} /></button>
                </div>
                {loading ? <div className="p-6 text-[#9AA2AD]">Caricamento template…</div> : (
                    <div className="p-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div className="space-y-3">
                            <Field label="Template *" select
                                options={templates.map(t => ({ v: t.id, l: `[${t.categoria}] ${t.titolo}` }))}
                                value={selectedTpl} onChange={setSelectedTpl} testid="gen-tpl" />

                            <Field label="Ingaggio collegato (per compenso/data)" select
                                options={[{ v: "", l: "— Nessun ingaggio (usa dati anagrafica) —" },
                                          ...engagements.map(e => ({ v: e.id, l: `${e.data_prestazione} · ${e.evento || "?"} · €${e.compenso_lordo}` }))]}
                                value={selectedEng} onChange={setSelectedEng} testid="gen-eng" />

                            {isDelega && (
                                <div className="border border-[#4B7BEA]/30 bg-[#4B7BEA]/5 p-3 space-y-2">
                                    <div className="text-[10px] uppercase tracking-widest text-[#4B7BEA] flex items-center gap-1">
                                        <IdCard size={11} /> Delega Targa Prova — Collega a contratto (opzionale)
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                        <Field label="Rif. contratto" value={extra.rif_contratto} onChange={v => setExtra(prev => ({ ...prev, rif_contratto: v }))} placeholder="es. n. 001/2026" testid="gen-rif-contr" />
                                        <Field label="Data contratto" type="date" value={extra.data_contratto} onChange={v => setExtra(prev => ({ ...prev, data_contratto: v }))} testid="gen-data-contr" />
                                    </div>
                                    <Field label="Validità delega (testo)" value={extra.validita_targa}
                                        onChange={v => setExtra(prev => ({ ...prev, validita_targa: v }))}
                                        placeholder="es. per la sola giornata del 15/09/2026" colSpan={2} testid="gen-validita" />
                                </div>
                            )}

                            {!isDelega && (
                                <div className="border border-[#9AA2AD]/20 p-3 space-y-2">
                                    <div className="text-[10px] uppercase tracking-widest text-[#9AA2AD]">Opzioni contratto</div>
                                    <div className="grid grid-cols-2 gap-2">
                                        <Field label="Luogo firma" value={extra.luogo_firma} onChange={v => setExtra(prev => ({ ...prev, luogo_firma: v }))} placeholder="Foglianise" testid="gen-luogo" />
                                        <Field label="Giorni pagamento" value={extra.giorni_pagamento} onChange={v => setExtra(prev => ({ ...prev, giorni_pagamento: v }))} testid="gen-giorni" />
                                        <Field label="Recesso rimborso" value={extra.recesso_rimborso} onChange={v => setExtra(prev => ({ ...prev, recesso_rimborso: v }))} placeholder="nulla / € 50" testid="gen-recesso" />
                                        <Field label="RCT estesa"
                                            select options={[{ v: "di aver esteso", l: "di aver esteso" }, { v: "di verificare l'estensione", l: "di verificare l'estensione" }]}
                                            value={extra.rct_estesa} onChange={v => setExtra(prev => ({ ...prev, rct_estesa: v }))} testid="gen-rct" />
                                    </div>
                                </div>
                            )}

                            <button onClick={download} disabled={busyPdf || !selectedTpl}
                                className="w-full bg-[#E2231A] hover:bg-[#c11e15] disabled:opacity-40 text-white px-4 py-3 text-[12px] uppercase tracking-widest flex items-center justify-center gap-2"
                                data-testid="gen-download">
                                <Download size={14} /> {busyPdf ? "Generazione…" : "Scarica PDF"}
                            </button>
                        </div>

                        <div>
                            <div className="text-[10px] uppercase tracking-widest text-[#9AA2AD] mb-2 flex items-center gap-2">
                                <Eye size={11} /> Anteprima testo (placeholder risolti)
                            </div>
                            <pre className="bg-[#0F1114] border border-[#9AA2AD]/20 p-3 text-[10px] leading-relaxed text-[#F4F2ED] whitespace-pre-wrap font-mono max-h-[70vh] overflow-y-auto">
                                {preview || "Seleziona un template per vedere l'anteprima…"}
                            </pre>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

// ═════════════════════════════ REGISTRO PROVA ═════════════════════════════
const EMPTY_REG = {
    scheda_num: "", data_prova: new Date().toISOString().slice(0, 10),
    ora_inizio: "", ora_fine: "",
    veic_marca_modello: "", veic_vin: "", veic_targa_imm: "",
    finalita_tecnica: false, finalita_messa_punto: false, finalita_dimostrativa: false,
    finalita_trasferimento: false, finalita_allestimento: false, finalita_altro: false,
    fin_altro_testo: "",
    cond_nome: "", cond_qualita: "delegato", cond_pat_cat: "", cond_pat_num: "",
    pass_nome: "", pass_qualita: "none",
    percorso: "", esito_note: "",
};

function RegistroProvaTab({ token }) {
    const [f, setF] = useState(EMPTY_REG);
    const [collabs, setCollabs] = useState([]);
    const [busy, setBusy] = useState(false);
    const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));
    const toggle = (k) => setF(prev => ({ ...prev, [k]: !prev[k] }));

    useEffect(() => {
        (async () => {
            try { const r = await teamListCollab(token); setCollabs(r.items || []); } catch (e) { }
        })();
    }, [token]);

    const selectCollabAsCond = (id) => {
        const c = collabs.find(x => x.id === id);
        if (!c) return;
        setF(prev => ({
            ...prev,
            cond_nome: `${c.cognome} ${c.nome}`.trim(),
            cond_pat_cat: c.patente_cat || prev.cond_pat_cat,
            cond_pat_num: c.patente_num || prev.cond_pat_num,
        }));
    };

    const selectCollabAsPass = (id) => {
        const c = collabs.find(x => x.id === id);
        if (!c) return;
        setF(prev => ({ ...prev, pass_nome: `${c.cognome} ${c.nome}`.trim() }));
    };

    const download = async (blank = false) => {
        setBusy(true);
        try {
            const payload = blank ? { ...EMPTY_REG, data_prova: "" } : f;
            const blob = await teamRegistroProvaPdf(token, payload);
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `registro_prova_${blank ? "vuoto" : (f.data_prova || "oggi")}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) { alert(e.response?.data?.detail || "Errore generazione PDF"); }
        finally { setBusy(false); }
    };

    const CheckRow = ({ k, label, testid }) => (
        <label className="flex items-center gap-2 cursor-pointer select-none py-1" data-testid={testid}>
            <input type="checkbox" checked={f[k]} onChange={() => toggle(k)}
                className="w-4 h-4 accent-[#E2231A]" />
            <span className="text-[#F4F2ED]">{label}</span>
        </label>
    );

    return (
        <div className="space-y-4" data-testid="registro-prova-tab">
            <div className="border border-[#4B7BEA]/30 bg-[#4B7BEA]/5 p-3 text-xs text-[#F4F2ED]">
                🧾 <strong>Documento facoltativo</strong> — attesta la finalità della circolazione di prova. Da tenere a bordo insieme all'autorizzazione ed eventuale delega. Puoi generarlo <strong>compilato</strong> (per registrare una prova reale) oppure <strong>vuoto</strong> (da compilare a mano in pista).
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Colonna sinistra */}
                <div className="space-y-4">
                    <Section title="Scheda">
                        <Field label="Scheda n." value={f.scheda_num} onChange={v => set("scheda_num", v)} placeholder="001" testid="reg-scheda-num" />
                        <Field label="Data prova" type="date" value={f.data_prova} onChange={v => set("data_prova", v)} testid="reg-data" />
                        <Field label="Ora inizio" type="time" value={f.ora_inizio} onChange={v => set("ora_inizio", v)} testid="reg-ora-in" />
                        <Field label="Ora fine" type="time" value={f.ora_fine} onChange={v => set("ora_fine", v)} testid="reg-ora-fin" />
                    </Section>

                    <Section title="Veicolo">
                        <Field label="Marca e modello" value={f.veic_marca_modello} onChange={v => set("veic_marca_modello", v)} placeholder="Es. Subaru Impreza WRX STI" colSpan={2} testid="reg-marca" />
                        <Field label="Telaio (VIN)" value={f.veic_vin} onChange={v => set("veic_vin", v.toUpperCase())} mono testid="reg-vin" />
                        <Field label="Targa immatricolazione (se presente)" value={f.veic_targa_imm} onChange={v => set("veic_targa_imm", v.toUpperCase())} mono testid="reg-targa-imm" />
                    </Section>

                    <div>
                        <div className="text-[10px] uppercase tracking-widest text-[#9AA2AD] mb-2 border-b border-[#9AA2AD]/10 pb-1">Finalità della prova (barra una o più)</div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-1 text-sm">
                            <CheckRow k="finalita_tecnica" label="Prova tecnica / collaudo" testid="reg-fin-tecnica" />
                            <CheckRow k="finalita_messa_punto" label="Messa a punto meccanica" testid="reg-fin-messa" />
                            <CheckRow k="finalita_dimostrativa" label="Prova dimostrativa (vendita)" testid="reg-fin-dim" />
                            <CheckRow k="finalita_trasferimento" label="Trasferimento" testid="reg-fin-tras" />
                            <CheckRow k="finalita_allestimento" label="Allestimento" testid="reg-fin-alle" />
                            <CheckRow k="finalita_altro" label="Altro (specifica sotto)" testid="reg-fin-altro" />
                        </div>
                        {f.finalita_altro && (
                            <input type="text" value={f.fin_altro_testo} onChange={e => set("fin_altro_testo", e.target.value)}
                                placeholder="Specifica altro…" className="mt-2 w-full bg-[#0F1114] border border-[#9AA2AD]/20 text-[#F4F2ED] px-3 py-2 text-sm"
                                data-testid="reg-fin-altro-testo" />
                        )}
                    </div>
                </div>

                {/* Colonna destra */}
                <div className="space-y-4">
                    <Section title="Conducente">
                        <Field label="Seleziona dal team (opzionale)"
                            select
                            options={[{ v: "", l: "— Manuale —" }, ...collabs.map(c => ({ v: c.id, l: `${c.cognome} ${c.nome}` }))]}
                            value="" onChange={selectCollabAsCond} colSpan={2} testid="reg-cond-select" />
                        <Field label="Nome e cognome" value={f.cond_nome} onChange={v => set("cond_nome", v)} colSpan={2} testid="reg-cond-nome" />
                        <Field label="Patente categoria" value={f.cond_pat_cat} onChange={v => set("cond_pat_cat", v.toUpperCase())} testid="reg-cond-pat-cat" />
                        <Field label="Patente numero" value={f.cond_pat_num} onChange={v => set("cond_pat_num", v)} mono testid="reg-cond-pat-num" />
                    </Section>
                    <div>
                        <div className="text-[10px] uppercase tracking-widest text-[#9AA2AD] mb-2">Qualità conducente</div>
                        <div className="space-y-1 text-sm">
                            {[
                                { v: "titolare", l: "Titolare / legale rappresentante" },
                                { v: "delegato", l: "Dipendente / collaboratore delegato" },
                                { v: "cliente", l: "Cliente / potenziale acquirente" },
                            ].map(o => (
                                <label key={o.v} className="flex items-center gap-2 cursor-pointer" data-testid={`reg-cond-q-${o.v}`}>
                                    <input type="radio" name="cond_qualita" checked={f.cond_qualita === o.v} onChange={() => set("cond_qualita", o.v)} className="w-4 h-4 accent-[#E2231A]" />
                                    <span className="text-[#F4F2ED]">{o.l}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    <Section title="Passeggero (opzionale)">
                        <Field label="Seleziona dal team (opzionale)"
                            select
                            options={[{ v: "", l: "— Manuale / Nessuno —" }, ...collabs.map(c => ({ v: c.id, l: `${c.cognome} ${c.nome}` }))]}
                            value="" onChange={selectCollabAsPass} colSpan={2} testid="reg-pass-select" />
                        <Field label="Nome e cognome" value={f.pass_nome} onChange={v => set("pass_nome", v)} colSpan={2} testid="reg-pass-nome" />
                    </Section>
                    <div>
                        <div className="text-[10px] uppercase tracking-widest text-[#9AA2AD] mb-2">Qualità passeggero</div>
                        <div className="space-y-1 text-sm">
                            {[
                                { v: "none", l: "Nessun passeggero / non pertinente" },
                                { v: "titolare", l: "Titolare / legale rappresentante" },
                                { v: "delegato", l: "Dipendente / collaboratore delegato" },
                            ].map(o => (
                                <label key={o.v} className="flex items-center gap-2 cursor-pointer" data-testid={`reg-pass-q-${o.v}`}>
                                    <input type="radio" name="pass_qualita" checked={f.pass_qualita === o.v} onChange={() => set("pass_qualita", o.v)} className="w-4 h-4 accent-[#E2231A]" />
                                    <span className="text-[#F4F2ED]">{o.l}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    <Section title="Percorso ed esito">
                        <Field label="Percorso / località" value={f.percorso} onChange={v => set("percorso", v)} placeholder="Es. Autodromo di Modena — sessioni pista" colSpan={2} testid="reg-percorso" />
                        <Field label="Esito / note" textarea rows={2} value={f.esito_note} onChange={v => set("esito_note", v)} colSpan={2} testid="reg-note" />
                    </Section>
                </div>
            </div>

            <div className="border-t border-[#9AA2AD]/20 pt-4 flex flex-wrap gap-2 justify-end">
                <button onClick={() => download(true)} disabled={busy}
                    className="border border-[#9AA2AD]/40 hover:bg-[#9AA2AD]/10 text-[#9AA2AD] px-4 py-3 text-[11px] uppercase tracking-widest flex items-center gap-2"
                    data-testid="reg-download-blank">
                    <Download size={13} /> Scarica PDF Vuoto (da compilare a mano)
                </button>
                <button onClick={() => download(false)} disabled={busy}
                    className="bg-[#E2231A] hover:bg-[#c11e15] disabled:opacity-40 text-white px-4 py-3 text-[11px] uppercase tracking-widest flex items-center gap-2"
                    data-testid="reg-download-filled">
                    <Download size={13} /> {busy ? "Generazione…" : "Scarica PDF Compilato"}
                </button>
            </div>
        </div>
    );
}

